import {
  AutocompleteInput,
  BooleanInput,
  Create,
  DateInput,
  NumberInput,
  ReferenceInput,
  SimpleForm,
  TextInput,
} from "@/components/admin";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, FileText, Landmark, Receipt } from "lucide-react";
import { required, useGetList, useGetOne, useNotify, useRedirect } from "ra-core";
import type { Company } from "../../types";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { AutoPopulateCompanyDetails } from "../shared/AutoPopulateCompanyDetails";
import { AutoReferenceField } from "../shared/AutoReferenceField";
import { CustomerNameWatcher } from "../shared/CustomerNameWatcher";
import { DocumentItemsTable } from "../shared/DocumentItemsTable";
import { QuotationMarkupField } from "./QuotationMarkup";
import { GrandTotalsWatcher } from "../shared/GrandTotalsWatcher";
import { TermsTemplateSelector } from "../shared/TermsTemplateSelector";
import { requireNonZeroTotal } from "../shared/validators";
import { FilteredContactInput } from "../../misc/FilteredContactInput";
/**
 * Warns when the selected company has no address on record (ID:31), so the user
 * knows the blank Bill To / Ship To fields are expected and can either add the
 * address to the company master or type one in manually below.
 */
const CompanyAddressNotice = () => {
  const companyId = useWatch({ name: "company_id" });
  const { data: company } = useGetOne<Company>(
    "companies",
    { id: companyId },
    { enabled: !!companyId },
  );

  if (!companyId || !company) return null;

  const hasAnyAddress = Boolean(
    company.address ||
      company.city ||
      company.zipcode ||
      company.state_abbr ||
      (company as any).billing_address ||
      (company as any).shipping_address,
  );
  if (hasAnyAddress) return null;

  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>
        No address found for this company.{" "}
        <Link
          to={`/companies/${companyId}`}
          className="font-semibold underline hover:no-underline"
        >
          Add it to the company master
        </Link>
        , or enter the Bill To / Ship To address manually below.
      </span>
    </div>
  );
};

/** Billing + shipping address with a "same as billing" shortcut (QTN #19). */
const QuotationAddressFields = () => {
  const { setValue } = useFormContext();
  const sameAsBilling = useWatch({ name: "_shipping_same_as_billing" });
  const billing = useWatch({ name: "billing_address" });

  useEffect(() => {
    if (sameAsBilling) {
      setValue("shipping_address", billing || "", { shouldDirty: true });
    }
  }, [sameAsBilling, billing, setValue]);

  return (
    <div className="space-y-4 pt-2">
      <CompanyAddressNotice />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TextInput
          source="billing_address"
          label="Billing Address"
          multiline
          rows={3}
          className="w-full"
        />
        {!sameAsBilling && (
          <TextInput
            source="shipping_address"
            label="Shipping Address"
            multiline
            rows={3}
            className="w-full"
          />
        )}
      </div>
      <BooleanInput
        source="_shipping_same_as_billing"
        label="Shipping address same as billing"
        helperText={false}
      />
    </div>
  );
};

const QuotationCreateTitle = () => (
  <div className="flex items-center gap-3">
    <Button
      asChild
      variant="outline"
      size="sm"
      className="gap-1.5 bg-[#00a68d]/10 hover:bg-[#00a68d]/20 text-[#00a68d] hover:text-[#00947e] border border-[#00a68d]/20 hover:border-[#00a68d]/30 font-semibold cursor-pointer h-9 px-4 rounded-lg"
    >
      <Link to="/quotations" className="flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4 text-[#00a68d]" /> Back
      </Link>
    </Button>
    <span className="text-xl font-bold tracking-tight">Create Quotation</span>
  </div>
);

export const QuotationCreate = () => {
  const location = useLocation();
  const sourceRecord = location.state?.record || {};
  const queryClient = useQueryClient();
  const redirect = useRedirect();
  const notify = useNotify();

  const { data: terms } = useGetList("terms_templates", {
    pagination: { page: 1, perPage: 1 },
    filter: { is_default: true },
  });

  const defaultTerms = terms && terms.length > 0 ? terms[0].content : "";

  const mutationOptions = {
    onSuccess: (data: any) => {
      if (sourceRecord.project_id) {
        queryClient.invalidateQueries({ queryKey: ["quotations"] });
      }
      notify("ra.notification.created", { type: "info", messageArgs: { smart_count: 1 } });
      // Land on the document preview instead of staying in the edit form
      redirect(`/quotations/${data.id}/show`);
    },
  };

  return (
    <Create
      title={<QuotationCreateTitle />}
      record={{ terms_conditions: defaultTerms, ...sourceRecord }}
      mutationOptions={mutationOptions}
    >
      <SimpleForm className="max-w-5xl mx-auto space-y-6" validate={requireNonZeroTotal}>
        <AutoPopulateCompanyDetails />
        <Card className="w-full shadow-sm">
          <CardHeader className="pb-3 border-b bg-slate-50/50">
            <div className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg font-semibold tracking-tight">
                New Quotation
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* Row 1: Quotation No, Date, Valid Until */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <AutoReferenceField docType="quotation" />
              <DateInput
                source="date"
                defaultValue={new Date()}
                validate={required()}
                className="w-full"
              />
              <DateInput
                source="valid_until"
                label="Valid Until"
                className="w-full"
              />
            </div>

            {/* Row 2: Company + Customer Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
              <ReferenceInput source="company_id" reference="companies">
                <AutocompleteInput
                  label="Company"
                  validate={required()}
                  className="w-full"
                />
              </ReferenceInput>

              <FilteredContactInput source="customer_id" label="Customer Contact" className="w-full" />

              <TextInput source="project_id" className="hidden" />
            </div>

            <QuotationAddressFields />
          </CardContent>
        </Card>

        <Card className="w-full shadow-sm border-t-4 border-t-primary">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg font-semibold tracking-tight">
                Line Items & Financials
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Two-tier quotation: Operations enter Cost per line; Sales set markup % */}
            <QuotationMarkupField />
            <DocumentItemsTable showCost showSubItems />
            <GrandTotalsWatcher />
            <CustomerNameWatcher />

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 p-4 rounded-lg border">
              <NumberInput
                source="cgst_amount"
                label="CGST (₹)"
                readOnly
                className="w-full"
              />
              <NumberInput
                source="sgst_amount"
                label="SGST (₹)"
                readOnly
                className="w-full"
              />
              <NumberInput
                source="igst_amount"
                label="IGST (₹)"
                helperText="Enter manually for interstate"
                className="w-full"
              />
              <NumberInput
                source="total_amount"
                label="Grand Total (₹)"
                readOnly
                className="w-full font-bold text-primary"
              />
              <div className="md:col-span-4">
                <TextInput
                  source="amount_in_words"
                  label="Amount in Words"
                  className="w-full italic"
                  readOnly
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="w-full shadow-sm">
          <CardHeader className="pb-3 border-b bg-slate-50/50">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                <CardTitle className="text-lg font-semibold tracking-tight">
                  Terms & Notes
                </CardTitle>
              </div>
              <TermsTemplateSelector type="quotation" />
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <TextInput
              source="terms_conditions"
              label="Terms & Conditions"
              multiline
              rows={4}
              className="w-full max-w-none"
            />
            <TextInput
              source="notes"
              label="Internal Notes"
              multiline
              rows={2}
              className="w-full max-w-none"
            />
          </CardContent>
        </Card>
      </SimpleForm>
    </Create>
  );
};
